import { useCallback, useEffect, useState } from 'react';
import { usePostMessage } from '../context/messageContext';
import { Button, Flex } from '../2022-component-library';
import { theme } from '../component-library';
import { logmarToSnellen } from '../helpers/data';
import { useMaxLogmar } from '../helpers/useMaxLogmar';
import styled from '@emotion/styled';
import { useTestContext } from '../context/testContext';

const StyledTable = styled.table`
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  background: #ffffff;
  overflow: hidden;
  border-collapse: collapse;
  thead {
    background: rgba(0, 0, 0, 0.05);
    width: 100%;
    th {
      border: thin solid ${theme.colors.grey};
      padding: 8px;
      color: #000;
      text-align: center;
      font-size: 14px;
      &:nth-of-type(1) {
        span {
          padding-left: 20px;
        }
      }
      &.logmar-scale,
      &.snellen-scale {
        font-weight: bold;
      }
    }
  }
  tbody {
    tr {
      width: 100%;
      &:last-of-type {
        border-bottom: 0px;
      }
      td {
        border: thin solid ${theme.colors.grey};
        text-align: center;
        padding: 5px;
        color: #000;
        font-size: 14px;
        position: relative;
        svg {
          &:hover {
            cursor: pointer;
          }
        }
        &:nth-of-type(1) {
          box-sizing: border-box;
        }
        &.checkbox {
          background: rgba(0, 0, 0, 0.05);
          width: 130px;
        }
        &.logmar-scale,
        &.snellen-scale {
          width: 100px;
          font-weight: bold;
        }
        &.logmar {
          width: 130px;
          font-size: 1rem;
        }
        &.logmar .char-container {
          display: flex;
          place-content: space-evenly;
        }
        &.logmar span {
          transition: all 0.2s ease;
          border: 1px solid transparent;
          width: 15px;
          padding: 0rem 0.4rem;
        }
        #${({ highlightedCharacter }) => highlightedCharacter} {
          border: 1px solid black;
          background-color: #ddffdd;
        }
        .active-logmar-char {
          ${({ testInProgress }) =>
            testInProgress &&
            `cursor: pointer;
        &:hover {
        border: 1px solid black;
      }`};
        }
      }
    }
    .disabled {
      background-color: lightgrey;
      pointer-events: none;
      td {
        color: grey;
        vertical-align: middle;
        padding: 6px;
      }
    }
  }
`;

const LogmarTestTable = ({ chartData, testInProgress, currentLogmar, handleSelection, logmarCharacters, displayCharacters }) => {
  const [highlightedCharacter, setHighlightedCharacter] = useState('');
  const postMessage = usePostMessage();
  const usingOptimaChart = typeof chartData[0].value === 'string';
  const updateMaxLogmar = useMaxLogmar();
  const {
    testData: { maxPermittedLogmar },
  } = useTestContext();

  const handleCharacterHighlight = useCallback(
    (charId) => {
      postMessage({ type: 'highlightLogmarCharacter', content: charId });
      return setHighlightedCharacter(charId);
    },
    [postMessage],
  );

  useEffect(() => {
    handleCharacterHighlight(null);
  }, [currentLogmar, handleCharacterHighlight]);

  useEffect(() => {
    updateMaxLogmar();
  }, [updateMaxLogmar]);

  return (
    <Flex column gap='0.75rem'>
      {testInProgress && (
        <Button
          fontFamily='Plus Jakarta Sans'
          width='17em'
          backgroundColor={theme.colors.negativeDark}
          disabled={!highlightedCharacter}
          cursor={highlightedCharacter ? 'pointer' : 'auto'}
          opacity={highlightedCharacter ? '1' : '0'}
          onClick={() => handleCharacterHighlight(null)}
        >
          Clear Highlighted Character
        </Button>
      )}
      <StyledTable testInProgress={testInProgress} highlightedCharacter={highlightedCharacter}>
        <thead>
          <tr>
            <th>Selection</th>
            <th className='logmar-scale'>{!usingOptimaChart ? 'logMAR' : 'Size'}</th>
            {!usingOptimaChart && <th className='snellen-scale'>Snellen</th>}
            {logmarCharacters && displayCharacters ? <th>Characters</th> : null}
          </tr>
        </thead>
        <tbody>
          {chartData.map((logmar) => {
            const charsForThisLogmar = logmarCharacters ? logmarCharacters[logmar.id]?.split(' ') : [];
            const logmarDisabled = !usingOptimaChart && logmar?.id < maxPermittedLogmar?.id;
            return (
              <tr key={logmar.value} {...(logmarDisabled && { className: 'disabled' })}>
                {logmarDisabled ? (
                  <td>Not Available</td>
                ) : (
                  <td className='checkbox'>
                    {testInProgress ? (
                      <input type='radio' name='checkbox' checked={logmar?.id === currentLogmar?.id} readOnly />
                    ) : (
                      <input
                        type='radio'
                        name='checkbox'
                        onChange={() => handleSelection(logmar, logmarCharacters ? logmarCharacters[logmar.id] : null)}
                      />
                    )}
                  </td>
                )}
                <td className='logmar-scale'>{logmar.value}</td>
                {!usingOptimaChart && <td className='snellen-scale'>{logmarToSnellen[logmar.value.toFixed(1)]}</td>}
                {logmarCharacters && displayCharacters ? (
                  <td className='logmar'>
                    <div className='char-container'>
                      {charsForThisLogmar?.map((char) => (
                        <span
                          key={`logmar-id-${logmar.id}-char-${char}`}
                          id={`logmar-id-${logmar.id}-char-${char}`}
                          className={logmar === currentLogmar ? 'active-logmar-char' : null}
                          onClick={logmar === currentLogmar ? (e) => handleCharacterHighlight(e.target.id) : null}
                        >
                          {char}
                        </span>
                      ))}
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
    </Flex>
  );
};

export default LogmarTestTable;
